import { useSyncExternalStore } from "react";

/**
 * ============================================================================
 *  etatNavigation.ts — L'état de la navigation, partagé et durable
 * ----------------------------------------------------------------------------
 *  Ce que la navigation « carnet » montre (ouverte ou non, espace affiché,
 *  sous-zone dépliée) vit ici plutôt que dans `SceneNav` : trois composants s'y
 *  croisent, et un état local se perdrait au démontage.
 *
 *    SceneNav          lit et écrit l'état ; prend l'INSTANTANÉ avant chaque
 *                      changement de scène ; remet défilement et focus après
 *                      une restauration ;
 *    SceneTitle        se signale (`titre`), ce qui masque la navigation sur
 *                      ordinateur ; à sa fin, RESTAURE l'instantané ;
 *    les obstacles bas la barre de navigation mobile et le contrôleur
 *                      d'ambiances déclarent la place qu'ils prennent au bas
 *                      de l'écran (`bas`), mesurée : le titre se pose au-dessus.
 *
 *  L'instantané est celui d'AVANT le premier changement : des scènes
 *  enchaînées pendant un même titre ne l'écrasent pas, c'est l'état que le
 *  visiteur avait sous les yeux qu'on lui rend.
 *
 *  Sauf la sous-zone dépliée : elle suit la scène d'ARRIVÉE, pas celle de
 *  départ. Rendre l'ancienne replierait la liste où se trouve la scène qu'on
 *  vient d'atteindre (entrée dans la grange par un hotspot : « RDC » fermé).
 *  Elle n'est donc pas dans l'instantané.
 *
 *  Un petit store à abonnement, sans dépendance : `creerEtatNavigation` pour
 *  les tests, `etatNavigation` pour l'application.
 * ==========================================================================*/

export interface Instantane {
  ouvert: boolean;
  espaceVu: string | null;
  /** Défilement de la liste du panneau, en pixels. */
  defilement: number;
  /** Clé (`data-sn-cle`) de l'élément du panneau qui avait le focus ; `null` : focus ailleurs. */
  focus: string | null;
}

export interface EtatNavigation {
  ouvert: boolean;
  /** Espace affiché sur le plan (téléphone) ; `null` : celui de la scène courante. */
  espaceVu: string | null;
  /** La seule sous-zone dépliée ; `null` : toutes repliées. */
  sousZone: string | null;
  /** Le titre d'arrivée est à l'écran (ordinateur : la navigation s'efface). */
  titre: boolean;
  /** L'indicateur de rotation de la visite libre est à l'écran : pas de titre par-dessus. */
  indicateur: boolean;
  instantane: Instantane | null;
  /** Dernier instantané rendu : `SceneNav` y reprend défilement et focus. Un objet neuf à chaque fois. */
  restauration: Instantane | null;
  /** Place prise au bas de l'écran par chaque obstacle, en pixels depuis le bord bas. */
  bas: Readonly<Record<string, number>>;
}

const INITIAL: EtatNavigation = {
  ouvert: false,
  espaceVu: null,
  sousZone: null,
  titre: false,
  indicateur: false,
  instantane: null,
  restauration: null,
  bas: {},
};

export function creerEtatNavigation(depart: Partial<EtatNavigation> = {}) {
  let etat: EtatNavigation = { ...INITIAL, ...depart };
  const abonnes = new Set<() => void>();
  const poser = (suite: Partial<EtatNavigation>) => {
    etat = { ...etat, ...suite };
    for (const abonne of abonnes) abonne();
  };

  return {
    lire: () => etat,
    abonner: (abonne: () => void) => {
      abonnes.add(abonne);
      return () => void abonnes.delete(abonne);
    },

    setOuvert: (ouvert: boolean) => {
      if (etat.ouvert !== ouvert) poser({ ouvert });
    },
    setEspaceVu: (espaceVu: string | null) => {
      if (etat.espaceVu !== espaceVu) poser({ espaceVu });
    },
    setSousZone: (sousZone: string | null) => {
      if (etat.sousZone !== sousZone) poser({ sousZone });
    },
    setTitre: (titre: boolean) => {
      if (etat.titre !== titre) poser({ titre });
    },
    setIndicateur: (indicateur: boolean) => {
      if (etat.indicateur !== indicateur) poser({ indicateur });
    },

    /** Déclarer (ou retirer, `null`) la place d'un obstacle au bas de l'écran. */
    setBas: (cle: string, hauteur: number | null) => {
      if ((etat.bas[cle] ?? null) === hauteur) return;
      const bas = { ...etat.bas };
      if (hauteur === null) delete bas[cle];
      else bas[cle] = hauteur;
      poser({ bas });
    },

    /** Avant un changement de scène. Le premier instantané tient jusqu'à sa restauration. */
    memoriser: (dom: { defilement: number; focus: string | null }) => {
      if (etat.instantane) return;
      poser({
        instantane: { ouvert: etat.ouvert, espaceVu: etat.espaceVu, ...dom },
      });
    },

    /**
     * Fin du titre d'arrivée, à l'heure ou interrompu. Sur ordinateur, la
     * navigation revient dans l'état d'avant ; sur téléphone elle n'a jamais
     * quitté l'écran — on n'y touche pas, l'instantané est oublié.
     */
    finDuTitre: (mobile: boolean) => {
      if (mobile) {
        if (etat.instantane) poser({ instantane: null });
      } else if (etat.instantane) {
        const instantane = etat.instantane;
        poser({
          ouvert: instantane.ouvert,
          espaceVu: instantane.espaceVu,
          instantane: null,
          restauration: { ...instantane },
        });
      }
      if (etat.titre) poser({ titre: false });
    },

    /** Pas de titre (téléphone, indicateur) : l'instantané n'a plus d'objet. */
    oublier: () => {
      if (etat.instantane) poser({ instantane: null });
    },
  };
}

export type StoreNavigation = ReturnType<typeof creerEtatNavigation>;

export const etatNavigation = creerEtatNavigation();

export function useEtatNavigation<T>(choisir: (etat: EtatNavigation) => T): T {
  return useSyncExternalStore(etatNavigation.abonner, () => choisir(etatNavigation.lire()));
}

/** La place la plus haute prise au bas de l'écran ; 0 s'il n'y a rien. */
export const hauteurBas = (bas: EtatNavigation["bas"]): number => Math.max(0, ...Object.values(bas));
